import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {enqueueSnackbar} from 'notistack'
import {ActivationChannel} from '../../domain/entities/Activation.ts'
import {CrmCommand, HumanOutcome, LostReason, TeamMember} from '../../domain/entities/Pipeline.ts'
import {useCrmActivation, useCrmMutations} from '../../hooks/usePipelines.ts'
import {crmErrorMessage, dayCounter, memberById, newCommandId, travelerName, useDateFormatters} from './pipelineView.ts'
import {Avatar, Countries, CrmBadges, MarketChip, Tag} from './PipelineParts.tsx'
import {DetailPanelShell} from './DetailPanelShell.tsx'
import {CodeSection, ContactSection, HistorySection, NextTouchSection, NotesSection} from './DetailSections.tsx'
import {AddFollowUpDialog, MarkDoneDialog} from './PipelineDialogs.tsx'

type ActivationDetailPanelProps = {
    activationId: string
    team: TeamMember[]
    currentUserId: string | null
    onClose: () => void
    onRequestLost: (activationId: string, reason: LostReason | null, allowedReasons?: LostReason[]) => void
}

// Side panel for one activation (GET /crm/activations/{id}). Every action is a
// command; the mutation invalidates the board, the tasks and this panel.
export const ActivationDetailPanel: React.FC<ActivationDetailPanelProps> = ({activationId, team, currentUserId, onClose, onRequestLost}) => {
    const {t} = useTranslation('dashboard')
    const {formatTripRange} = useDateFormatters()
    const [channel, setChannel] = useState<ActivationChannel | undefined>(undefined)
    const {detail, isLoadingDetail, isFetchingDetail, detailError} = useCrmActivation(activationId, channel)
    const {sendCrmCommand} = useCrmMutations()
    const [doneOpen, setDoneOpen] = useState(false)
    const [followUpOpen, setFollowUpOpen] = useState(false)
    const [editContactRequest, setEditContactRequest] = useState(0)
    const titleId = 'pl-panel-title'

    // A different activation starts on its default channel.
    useEffect(() => setChannel(undefined), [activationId])

    const sendCommand = (command: CrmCommand, successKey: string) =>
        sendCrmCommand({id: activationId, command})
            .then((result) => {
                enqueueSnackbar(t(successKey), {variant: 'success'})
                return result
            })
            .catch((error) => {
                enqueueSnackbar(crmErrorMessage(error, t), {variant: 'error'})
                throw error
            })

    // Dialog submits surface their own error inline, so no error toast there.
    const sendFromDialog = (command: CrmCommand, successKey: string) =>
        sendCrmCommand({id: activationId, command}).then(() => enqueueSnackbar(t(successKey), {variant: 'success'}))

    const activation = detail?.activation
    const touch = detail?.nextTouch?.touchpoint
    const preparedChannel = detail?.nextTouch?.preparedMessage?.channel
    const defaultChannel: ActivationChannel = channel ?? preparedChannel ?? touch?.channel ?? activation?.traveler.preferredChannel ?? 'EMAIL'
    const name = activation ? travelerName(activation.traveler, t('pipelines.card.noName')) : ''
    const accountManager = memberById(team, detail?.agency.accountManagerId)
    const accountManagerName = detail?.agency.accountManagerName ?? accountManager?.name ?? null
    const counter = activation ? dayCounter(activation.trip, t) : null
    const canMarkLost = !!detail?.allowedCommands?.includes('MARK_LOST')

    return (
        <>
            <DetailPanelShell
                entityKey={activationId} titleId={titleId} isRefreshing={isFetchingDetail && !isLoadingDetail} onClose={onClose}
                topLine={activation && (
                    <>
                        <Countries countries={activation.trip.countries}/>
                        <span className="ml-1 text-[15px] font-bold tracking-[-.01em] text-neutralBlack-900 whitespace-nowrap">
                            {formatTripRange(activation.trip.startDate, activation.trip.endDate)}
                        </span>
                    </>
                )}
                title={isLoadingDetail ? t('pipelines.loading') : name || t('pipelines.panel.title')}
                header={detail && (
                            <>
                                <div className="flex items-center gap-2 mt-2">
                                    <Avatar name={detail.agency.name ?? detail.agency.id} size={24}/>
                                    <span className="text-[13px] text-neutralBlack-700">{detail.agency.name ?? detail.agency.id}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-[12.5px] text-neutralBlack-600">{t('pipelines.panel.accountManager')}</span>
                                    {accountManagerName
                                        ? <><Avatar member={accountManager} name={accountManagerName} size={22}/><span className="text-[13px] font-medium text-neutralBlack-900">{accountManagerName}</span></>
                                        : <span className="text-[13px] text-neutralBlack-700">{t('pipelines.unassigned')}</span>}
                                </div>
                                <div className="flex items-center flex-wrap gap-1.5 mt-3">
                                    <Tag tone="teal">{t(`pipelines.stage.${detail.crm.stage}`)}</Tag>
                                    <Tag>{t('activation.route.badge', {route: activation!.route})}</Tag>
                                    <MarketChip market={activation!.market}/>
                                    {counter && <Tag>{counter}</Tag>}
                                    {detail.crm.lostReason && <Tag>{t(`pipelines.lostReason.${detail.crm.lostReason}`)}</Tag>}
                                </div>
                                <div className="mt-2.5">
                                    <CrmBadges contactFailed={activation!.flags.contactFailed} pausedUntil={activation!.flags.pausedUntil}
                                               alerts={detail.crm.alerts} messagesSent={detail.crm.messagesSent}/>
                                </div>
                                {detail.crm.lostNote && <p className="mt-2 text-[12.5px] text-neutralBlack-700">{detail.crm.lostNote}</p>}
                            </>
                )}
                footer={canMarkLost && (
                    <button type="button" onClick={() => onRequestLost(activationId, null, detail?.lostReasons)}
                            className="h-9 px-3.5 rounded-[10px] border border-neutralBlack-200 bg-white text-[13px] font-medium text-neutralBlack-900 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out">
                        {t('pipelines.panel.markLost')}
                    </button>
                )}
            >
                    {isLoadingDetail ? (
                        <div className="px-6 py-5 flex flex-col gap-3">
                            {Array.from({length: 4}).map((_, i) => <div key={i} className="h-20 rounded-xl bg-neutralBlack-50 animate-pulse"/>)}
                        </div>
                    ) : detailError && !detail ? (
                        <div role="alert" className="px-6 py-10 text-center text-[13px] text-neutralBlack-700">{crmErrorMessage(detailError, t)}</div>
                    ) : detail && activation ? (
                        <>
                            {activation.flags.contactFailed && (
                                <div role="status" className="flex items-center gap-2.5 px-6 py-3 bg-[#FDECEA] text-[#B42318] border-b border-[#F8D3CE] text-[13px] font-medium">
                                    <span aria-hidden="true">!</span>
                                    <span>
                                        {t('pipelines.panel.contactFailedBanner', {
                                            channel: activation.flags.contactFailedChannel
                                                ? t(`pipelines.channel.${activation.flags.contactFailedChannel}`)
                                                : t('pipelines.panel.contact').toLowerCase(),
                                        })}
                                        {activation.flags.contactFailedReason && ` · ${activation.flags.contactFailedReason}`}
                                    </span>
                                </div>
                            )}
                            <NextTouchSection detail={detail} team={team} channel={channel} onChannelChange={setChannel}
                                              onMarkDone={() => setDoneOpen(true)} onAddFollowUp={() => setFollowUpOpen(true)}
                                              onEditContact={() => setEditContactRequest((n) => n + 1)}
                                              sendCommand={sendCommand} isFetching={isFetchingDetail}/>
                            <ContactSection traveler={activation.traveler} contactFailedChannel={activation.flags.contactFailedChannel}
                                            canEdit={!!detail.allowedCommands?.includes('UPDATE_CONTACT')}
                                            editRequest={editContactRequest} sendCommand={sendCommand}/>
                            <CodeSection detail={detail}/>
                            <NotesSection pipeline="activation" id={activationId} canAdd={detail.allowedCommands?.includes('ADD_NOTE') ?? true}/>
                            <HistorySection pipeline="activation" id={activation.id} history={detail.history} team={team}/>
                        </>
                    ) : null}
            </DetailPanelShell>

            {detail && (
                <>
                    <MarkDoneDialog isOpen={doneOpen} defaultChannel={defaultChannel} outcomes={detail.outcomes}
                                    deadline={activation?.redemptionDeadline ?? null}
                                    onClose={() => setDoneOpen(false)}
                                    onSubmit={({outcome, channel: usedChannel, date, note}) => sendFromDialog({
                                        commandId: newCommandId(), type: 'TOUCHPOINT_DONE', note: note || undefined,
                                        data: {touchpointId: touch?.id, channel: usedChannel, outcome: outcome as HumanOutcome, date},
                                    }, 'pipelines.feedback.done')}/>
                    <AddFollowUpDialog isOpen={followUpOpen} defaultChannel={defaultChannel} team={team} currentUserId={currentUserId}
                                       deadline={activation?.redemptionDeadline ?? null}
                                       onClose={() => setFollowUpOpen(false)}
                                       onSubmit={({date, channel: fuChannel, assignee, note}) => sendFromDialog({
                                           commandId: newCommandId(), type: 'ADD_FOLLOW_UP', note: note || undefined,
                                           data: {date, channel: fuChannel, assignee},
                                       }, 'pipelines.feedback.followUpAdded')}/>
                </>
            )}
        </>
    )
}
